import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ArticleCard } from '@/components/shared/ArticleCard';
import { EventCard } from '@/components/shared/EventCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { NewsletterSignup } from '@/components/shared/NewsletterSignup';
import { 
  useCachedArticles, 
  useCachedHeroArticle, 
  useCachedEvents 
} from '@/hooks/useCachedContent';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Car, Gauge, Users, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { SEOHead } from '@/components/seo/SEOHead';
import { OrganizationSchema } from '@/components/seo/StructuredData';

export default function HomePage() {
  const { data: heroArticle, isLoading: heroLoading } = useCachedHeroArticle();
  const { data: latestNews, isLoading: newsLoading } = useCachedArticles({ 
    contentType: 'news', 
    limit: 3 
  });
  const { data: featuredArticles, isLoading: featuredLoading } = useCachedArticles({ 
    featured: true, 
    limit: 4 
  });
  const { data: upcomingEvents, isLoading: eventsLoading } = useCachedEvents({ 
    upcoming: true, 
    limit: 2 
  });
  

  return (
    <Layout>
      <SEOHead
        title="Auto News Weekly - Automotive News, Reviews & Industry Events"
        description="Your trusted source for breaking automotive news, expert vehicle reviews, side-by-side comparisons, and industry events. Stay informed on EVs, trucks, SUVs, and more."
        canonical="https://www.autonewsweekly.com/"
      />
      <OrganizationSchema />
      
      {/* Hero Section - Mobile Optimized */}
      <section className="relative">
        {heroLoading ? (
          <div className="h-[50vh] md:h-[60vh] bg-muted animate-pulse" />
        ) : heroArticle ? (
          <Link 
            to={`/news/${heroArticle.slug}`}
            className="block relative h-[50vh] md:h-[60vh] lg:h-[70vh] min-h-[320px] md:min-h-[400px] lg:min-h-[500px] bg-primary overflow-hidden group"
          >
            {heroArticle.hero_image_url || heroArticle.thumbnail_url ? (
              <img
                src={heroArticle.hero_image_url || heroArticle.thumbnail_url}
                alt={heroArticle.title}
                className="absolute inset-0 w-full h-full object-cover object-[center_60%] transition-transform duration-500 group-hover:scale-105"
                width={1920}
                height={1080}
                loading="eager"
                fetchPriority="high"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary/80" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-12">
              <div className="container mx-auto">
                {heroArticle.categories?.[0] && (
                  <span className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-accent text-accent-foreground text-xs md:text-sm font-medium rounded mb-2 md:mb-4">
                    {heroArticle.categories[0].name}
                  </span>
                )}
                <h1 className="font-display text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white max-w-4xl mb-2 md:mb-4 leading-tight">
                  {heroArticle.title}
                </h1>
                {heroArticle.subtitle && (
                  <p className="text-sm md:text-lg lg:text-xl text-white/80 max-w-2xl mb-2 md:mb-4 line-clamp-2 md:line-clamp-none">
                    {heroArticle.subtitle}
                  </p>
                )}
                <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm">
                  {heroArticle.published_at && (
                    <span className="flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded">
                      <Calendar className="h-3 w-3 md:h-4 md:w-4" aria-hidden="true" />
                      <time dateTime={heroArticle.published_at} className="hidden sm:inline">
                        {format(new Date(heroArticle.published_at), 'MMMM d, yyyy')}
                      </time>
                      <time dateTime={heroArticle.published_at} className="sm:hidden">
                        {format(new Date(heroArticle.published_at), 'MMM d')}
                      </time>
                    </span>
                  )}
                  <span className="flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded">
                    <Clock className="h-3 w-3 md:h-4 md:w-4" aria-hidden="true" />
                    {heroArticle.read_time_minutes} min
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <div className="h-[40vh] bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center px-4">
            <div className="text-center text-primary-foreground">
              <h1 className="font-display text-2xl sm:text-4xl md:text-6xl font-bold mb-2 md:mb-4">Auto News Weekly</h1>
              <p className="text-base md:text-xl text-primary-foreground/80">Your source for automotive industry news</p>
            </div>
          </div>
        )}
      </section>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Latest News Section - Mobile Optimized Grid */}
        <section className="mb-10 md:mb-16" aria-labelledby="latest-news-heading">
          <SectionHeader title="Latest News" viewAllLink="/news" id="latest-news-heading" />
          {newsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[280px] md:h-[350px] rounded-lg" />
              ))}
            </div>
          ) : latestNews && latestNews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {latestNews.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No news articles yet.</p>
          )}
        </section>

        {/* Deal Builder - Mobile Optimized */}
        <section className="mb-10 md:mb-16 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5 rounded-xl md:rounded-2xl p-5 md:p-8 lg:p-12" aria-labelledby="deal-builder-heading">
          <div className="text-center mb-6 md:mb-10">
            <h2 id="deal-builder-heading" className="font-display text-xl md:text-2xl lg:text-3xl font-bold mb-2 md:mb-3">
              Build Your Best Deal
            </h2>
            <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
              Take control of your car buying experience with our AI-powered Deal Builder
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-10">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Car className="h-6 w-6 md:h-8 md:w-8 text-primary" />
              </div>
              <div className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold rounded-full mb-2 md:mb-3">
                Step 1
              </div>
              <h3 className="font-semibold text-base md:text-lg mb-1 md:mb-2">Build Your Offer</h3>
              <p className="text-muted-foreground text-xs md:text-sm">
                Choose the vehicle you want, name your price, and set the financing terms.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Gauge className="h-6 w-6 md:h-8 md:w-8 text-primary" />
              </div>
              <div className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold rounded-full mb-2 md:mb-3">
                Step 2
              </div>
              <h3 className="font-semibold text-base md:text-lg mb-1 md:mb-2">Get Your Deal IQ Score</h3>
              <p className="text-muted-foreground text-xs md:text-sm">
                Our AI scores your offer instantly and tells you how likely it is to be accepted.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Users className="h-6 w-6 md:h-8 md:w-8 text-primary" />
              </div>
              <div className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold rounded-full mb-2 md:mb-3">
                Step 3
              </div>
              <h3 className="font-semibold text-base md:text-lg mb-1 md:mb-2">Let Dealers Compete</h3>
              <p className="text-muted-foreground text-xs md:text-sm">
                Your offer is sent to nearby dealers who respond with real interest.
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/deals/builder">
              <Button size="default" className="gap-2 w-full sm:w-auto">
                Start Building Your Deal
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Newsletter - Mobile Optimized */}
        <section className="mb-10 md:mb-16 bg-secondary rounded-xl md:rounded-2xl p-5 md:p-8 lg:p-12" aria-labelledby="newsletter-heading">
          <div className="max-w-2xl mx-auto text-center">
            <h2 id="newsletter-heading" className="font-display text-xl md:text-2xl lg:text-3xl font-bold mb-2 md:mb-3">
              Stay in the Loop
            </h2>
            <p className="text-muted-foreground text-sm md:text-base mb-4 md:mb-6">
              Get exclusive automotive insights delivered directly to your inbox.
            </p>
            <NewsletterSignup showPreferences />
          </div>
        </section>

        {/* Featured Articles - Mobile Optimized */}
        <section className="mb-10 md:mb-16" aria-labelledby="featured-heading">
          <SectionHeader title="Featured Articles" viewAllLink="/featured" id="featured-heading" />
          {featuredLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-[250px] md:h-[300px] rounded-lg" />
              ))}
            </div>
          ) : featuredArticles && featuredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {featuredArticles.slice(0, 2).map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No featured articles yet.</p>
          )}
        </section>

        {/* Upcoming Events - Mobile Optimized */}
        <section className="mb-10 md:mb-16" aria-labelledby="events-heading">
          <SectionHeader title="Upcoming Events" viewAllLink="/events" id="events-heading" />
          {eventsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-[180px] md:h-[200px] rounded-lg" />
              ))}
            </div>
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No upcoming events.</p>
          )}
        </section>

      </main>
    </Layout>
  );
}
